import { GraphDefinition, ToolOverview, VulnerabilityFinding, RiskLevel } from '../types';

/**
 * Transform backend tools data to ToolOverview format
 */
export const transformToolsOverview = (graphData: GraphDefinition | null): ToolOverview[] => {
  if (!graphData?.tools) return [];
  
  return graphData.tools.map(tool => ({
    name: tool.name,
    category: (tool.intent as any)?.title || 'Tool',
    description: tool.description || 'No description',
    vulnerabilityCount: tool.vulnerabilities?.length || 0,
  }));
};

/**
 * Transform backend vulnerabilities to VulnerabilityFinding format
 * Sorted by severity: Critical -> High -> Medium -> Low -> Info
 */
export const transformVulnerabilityFindings = (graphData: GraphDefinition | null): VulnerabilityFinding[] => {
  if (!graphData) return [];
  
  const findings: VulnerabilityFinding[] = [];
  const today = new Date().toISOString().split('T')[0].replace(/-/g, '/');
  
  // Extract from tools
  graphData.tools?.forEach(tool => {
    tool.vulnerabilities?.forEach((vuln, idx) => {
      findings.push({
        id: `${tool.id}-vuln-${idx}`,
        name: vuln.name,
        severity: vuln.risk_level as RiskLevel,
        description: vuln.description,
        discoveredDate: today,
        updatedDate: today,
        affectedTool: tool.name,
        affectedToolId: tool.id,
        category: (tool.intent as any)?.title || 'Tool',
        securityFrameworkMapping: vuln.security_framework_mapping || {},
        recommendedGuardrails: vuln.guardrails?.map(g => g.name) || [],
        otherRecommendations: vuln.remediations || [],
        isExpanded: false,
      });
    });
  });

  // Extract from agents
  graphData.agents?.forEach(agent => {
    agent.vulnerabilities?.forEach((vuln, idx) => {
      findings.push({
        id: `${agent.id}-vuln-${idx}`,
        name: vuln.name,
        severity: (vuln.mitigation_level === 'None' ? 'Critical' : vuln.mitigation_level === 'Partial' ? 'High' : 'Low') as RiskLevel,
        description: vuln.guardrail_explanation || vuln.instruction_explanation || '',
        discoveredDate: today,
        updatedDate: today,
        affectedTool: agent.name,
        category: 'Agent',
        securityFrameworkMapping: {},
        recommendedGuardrails: [],
        otherRecommendations: [vuln.guardrail_explanation, vuln.instruction_explanation].filter(Boolean) as string[],
        isExpanded: false,
      });
    });
  });

  // Sort by severity: Critical -> High -> Medium -> Low -> Info
  const severityOrder: Record<string, number> = {
    'Critical': 0,
    'High': 1,
    'Medium': 2,
    'Low': 3,
    'Info': 4,
  };

  findings.sort((a, b) => {
    const aOrder = severityOrder[a.severity] ?? 5;
    const bOrder = severityOrder[b.severity] ?? 5;
    return aOrder - bOrder;
  });

  return findings;
};